import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Globe, ExternalLink, Mail, Tag, Shield, TrendingUp, Layers, Clock, Zap, Building2 } from "lucide-react";
import { getRiskBadge } from "./helpers";

export const QuickDetailModal = ({ isOpen, onOpenChange, item, type, onSave, isDemo }) => {
  if (!item) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-wider flex items-center gap-2">
            {type === "defi" && <Layers className="w-5 h-5 text-primary" />}
            {type === "stock" && <TrendingUp className="w-5 h-5 text-primary" />}
            {type === "web3" && <Building2 className="w-5 h-5 text-primary" />}
            <span className="truncate">{item._title || item.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Badges */}
          <div className="flex flex-wrap gap-1.5">
            {item._badges?.map((b, i) => (
              <Badge key={i} variant="outline" className="text-[10px]">{b}</Badge>
            ))}
            {item._risk != null && getRiskBadge(item._risk)}
          </div>

          {/* Cover image */}
          {item._image && (
            <div className="h-36 rounded-lg overflow-hidden bg-secondary/30">
              <img src={item._image} alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
          )}

          {/* Description */}
          {item._description && (
            <p className="text-sm text-muted-foreground leading-relaxed">{item._description}</p>
          )}

          {/* Metrics grid */}
          {item._metrics?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {item._metrics.map((m, i) => (
                <div key={i} className="p-3 bg-secondary/40 rounded-lg">
                  <p className="text-[10px] uppercase text-muted-foreground tracking-wider">{m.label}</p>
                  <p className={`font-mono font-bold text-sm ${m.color || ""}`}>{m.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Extra details */}
          {item._details?.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Details</p>
              <div className="bg-secondary/30 rounded-lg p-3 space-y-1.5">
                {item._details.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{d.label}</span>
                    <span className="font-mono font-medium">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-border">
            {!isDemo && onSave && (
              <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => { onSave(); onOpenChange(false); }} data-testid="quick-detail-select">
                <Tag className="w-3 h-3" /> Add to My Deals
              </Button>
            )}
            {item._url && (
              <Button variant="default" size="sm" className="text-xs gap-1 bg-primary text-primary-foreground" onClick={() => window.open(item._url, "_blank")} data-testid="quick-detail-visit">
                <ExternalLink className="w-3 h-3" /> Visit
              </Button>
            )}
            {item._contact && (
              <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => window.open(item._contact.includes("@") ? `mailto:${item._contact}` : item._contact, "_blank")}>
                <Mail className="w-3 h-3" /> Contact
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Build a detail object from a DeFi opportunity
export const buildDefiDetail = (opp) => ({
  _title: opp.name,
  _risk: opp.risk_score,
  _url: opp.url,
  _badges: [opp.protocol, opp.chain, opp.token, opp.is_manual ? "Manual" : "DeFi Llama"].filter(Boolean),
  _metrics: [
    { label: "APY", value: `${(opp.apy || 0).toFixed(1)}%`, color: opp.apy >= 20 ? "text-green-500" : opp.apy >= 10 ? "text-primary" : "" },
    { label: "TVL", value: `$${Math.round((opp.tvl || 0) / 1e6)}M` },
    { label: "Risk Score", value: `${opp.risk_score}/100` },
  ],
  _details: [
    { label: "Protocol", value: opp.protocol || "—" },
    { label: "Chain", value: opp.chain || "—" },
    { label: "Token", value: opp.token || "—" },
    { label: "Source", value: opp.is_manual ? "Manual entry" : "DeFi Llama API" },
  ],
});

// Build a detail object from a stock pick
export const buildStockDetail = (pick, sector, region) => ({
  _title: pick.name,
  _risk: pick.risk_score,
  _description: pick.rationale,
  _badges: [pick.ticker, pick.type, pick.currency, sector?.name, region?.name].filter(Boolean),
  _metrics: [
    { label: "Ticker", value: pick.ticker },
    { label: "Type", value: pick.type || "Stock" },
    { label: "Currency", value: pick.currency || "USD" },
    { label: "Risk Score", value: `${pick.risk_score}/100` },
    { label: "Horizon", value: pick.horizon || "—" },
  ],
  _details: [
    { label: "Region", value: region?.name || "—" },
    { label: "Regional Outlook", value: region?.outlook || "—" },
    { label: "Sector", value: sector?.name || "—" },
    { label: "Sector Trend", value: sector?.trend || "—" },
    ...(sector?.news_theme ? [{ label: "Driving Theme", value: sector.news_theme }] : []),
  ],
});

// Build a detail object from a Web3 Secondaries / Acquire.fi listing
export const buildWeb3Detail = (alt) => ({
  _title: alt.name,
  _risk: alt.risk_score,
  _description: alt.description,
  _image: alt.cover_url,
  _url: alt.detail_url,
  _contact: "team@acquire.fi",
  _badges: [alt.category, alt.listing_type, alt.status].filter(Boolean),
  _metrics: [
    { label: "Ticket Min", value: alt.ticket_min || "—" },
    { label: "Valuation", value: alt.valuation || "—" },
    { label: "Risk Score", value: `${alt.risk_score}/100` },
    { label: "Type", value: alt.listing_type || "—", color: alt.listing_type === "SELLING" ? "text-green-500" : "text-blue-500" },
  ],
  _details: [
    { label: "Category", value: alt.category || "—" },
    { label: "Listing Type", value: alt.listing_type || "—" },
    { label: "Status", value: alt.status || "—" },
    { label: "Source", value: "Acquire.fi OTC Secondaries" },
  ],
});
